import { Link } from 'react-router-dom'
import type { NotificationFeed, NotificationGroup } from '@/types/notifications'
import {
  composeNotificationVerdict,
  partitionByGroup,
} from '@/lib/notification-deck'
import { Button } from '@/components/carbon/Button'
import { IconArrowRight } from '@/components/carbon/icons'
import NotificationItem from './NotificationItem'

/**
 * Maximum visible items in the bell dropdown. Beyond this, the footer's "See
 * everything" link is the right surface — the dropdown stays a quick triage,
 * not a full list. 8 lines up with the surfacing-matrix line in
 * `.agents/Tasks/High Priority/message-center-notifications.md`.
 */
const MAX_VISIBLE = 8

const GROUP_LABEL: Record<NotificationGroup, string> = {
  overdue: 'Overdue',
  'due-soon': 'Due soon',
  'heads-up': 'Upcoming',
}

const GROUP_ORDER: NotificationGroup[] = ['overdue', 'due-soon', 'heads-up']

interface NotificationsDropdownProps {
  feed: NotificationFeed | undefined
  onDismiss: (key: string) => void
  onMarkAllRead: () => void
  /** Imperative-close hook from the Popover render prop. Called on footer
   *  link click so the dropdown collapses as you navigate away. */
  onClose: () => void
}

/**
 * The bell's menu: a Carbon layer-02 menu with the menu shadow, 380px wide
 * from 672px and full width on phones. It sits in the Gray 100 shell zone
 * (the bell's Popover panel carries data-shell and data-menu).
 *
 *   1. Header: "What needs attention" and the composed verdict line.
 *   2. Body: the top 8 items, under Overdue / Due soon / Upcoming headings.
 *   3. Footer: "See everything" and "Mark all read".
 */
export default function NotificationsDropdown({
  feed,
  onDismiss,
  onMarkAllRead,
  onClose,
}: NotificationsDropdownProps) {
  const verdict = composeNotificationVerdict(feed)
  const groups = partitionByGroup(feed?.items ?? [])

  // Walk groups in priority order, take up to MAX_VISIBLE total. We slice
  // per-section after concatenation so urgent rows always win the budget.
  const visibleByGroup: Record<NotificationGroup, typeof groups[NotificationGroup]> = {
    overdue: [],
    'due-soon': [],
    'heads-up': [],
  }
  let remaining = MAX_VISIBLE
  for (const group of GROUP_ORDER) {
    if (remaining <= 0) break
    visibleByGroup[group] = groups[group].slice(0, remaining)
    remaining -= visibleByGroup[group].length
  }

  const totalVisible =
    visibleByGroup.overdue.length +
    visibleByGroup['due-soon'].length +
    visibleByGroup['heads-up'].length

  const totalActive =
    groups.overdue.length + groups['due-soon'].length + groups['heads-up'].length

  return (
    <div className="flex max-h-[calc(100vh-64px)] w-full flex-col bg-layer-2 text-text-primary shadow-menu c-md:w-[380px]">
      <div className="flex-none border-b border-line-subtle px-4 py-3.5">
        <p className="m-0 text-body font-semibold leading-[1.29]">What needs attention</p>
        {totalActive > 0 ? <p className="m-0 mt-1 text-helper text-text-helper">{verdict}</p> : null}
      </div>

      {totalActive === 0 ? (
        <p className="m-0 px-4 py-8 text-center text-body text-text-secondary">Nothing to review.</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {GROUP_ORDER.map(group => {
            const rows = visibleByGroup[group]
            if (rows.length === 0) return null
            return (
              <section key={group} aria-labelledby={`notifications-${group}`}>
                <h3 id={`notifications-${group}`} className="m-0 px-4 pb-1 pt-3 text-helper font-normal text-text-helper">
                  {GROUP_LABEL[group]}
                </h3>
                <div>
                  {rows.map(item => (
                    <NotificationItem
                      key={item.key}
                      item={item}
                      onDismiss={onDismiss}
                      size="compact"
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}

      {totalVisible < totalActive && (
        <p className="m-0 flex-none border-t border-line-subtle px-4 py-2 text-center text-helper text-text-helper">
          {totalActive - totalVisible} more on the full feed.
        </p>
      )}

      <div className="flex flex-none items-center justify-between gap-4 border-t border-line-subtle py-1 pl-4 pr-1">
        <Link
          to="/notifications"
          onClick={onClose}
          className="inline-flex items-center gap-2 text-body text-link hover:text-link-hover hover:underline"
        >
          See everything
          <IconArrowRight />
        </Link>
        <Button
          kind="ghost"
          size="sm"
          onClick={onMarkAllRead}
          disabled={totalActive === 0 || (feed?.unreadCount ?? 0) === 0}
        >
          Mark all read
        </Button>
      </div>
    </div>
  )
}
